import React, { useState, useEffect, useCallback, useContext } from "react";
import moment from "moment";
import { makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  Paper,
  Typography,
  Box,
  Divider,
  CircularProgress,
} from "@material-ui/core";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import EventNoteIcon from "@material-ui/icons/EventNote";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const STATUS_COLORS = {
  confirmado: "#2563eb",
  concluido: "#16a34a",
  faltou: "#ea580c",
  cancelado: "#9ca3af",
};

const STATUS_LABELS = {
  confirmado: "Confirmado",
  concluido: "Concluido",
  faltou: "Faltou",
  cancelado: "Cancelado",
  pendente: "Pendente",
};

const PERIODS = [
  { key: "today", label: "Hoje" },
  { key: "week", label: "7 dias" },
  { key: "month", label: "30 dias" },
];

const formatCurrency = (val) =>
  Number(val).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  periodBar: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  periodBtn: {
    padding: "6px 18px",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 20,
    cursor: "pointer",
    fontSize: "0.85rem",
    fontWeight: 500,
    background: "none",
    color: theme.palette.text.secondary,
    transition: "all 0.15s",
    "&:hover": { borderColor: theme.palette.primary.main, color: theme.palette.primary.main },
  },
  periodBtnActive: {
    background: theme.palette.primary.main,
    color: "#fff",
    borderColor: theme.palette.primary.main,
    "&:hover": { background: theme.palette.primary.dark, borderColor: theme.palette.primary.dark, color: "#fff" },
  },
  card: {
    padding: theme.spacing(2.5),
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    height: "100%",
  },
  cardIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  cardValue: {
    fontWeight: 700,
    fontSize: "1.6rem",
    lineHeight: 1.1,
  },
  cardLabel: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  cardSub: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: theme.spacing(1),
  },
  statusDot: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  chartPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: "0.85rem",
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  rankRow: {
    display: "flex",
    alignItems: "center",
    padding: "6px 0",
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": { borderBottom: "none" },
  },
  rankPosition: {
    width: 24,
    fontWeight: 700,
    color: theme.palette.text.disabled,
    fontSize: "0.8rem",
    flexShrink: 0,
  },
  rankBar: {
    height: 6,
    borderRadius: 3,
    background: theme.palette.primary.main,
    marginTop: 3,
    opacity: 0.7,
  },
  upcomingRow: {
    display: "flex",
    alignItems: "center",
    padding: "8px 0",
    gap: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": { borderBottom: "none" },
  },
  upcomingTime: {
    fontWeight: 700,
    fontSize: "0.85rem",
    minWidth: 44,
    color: theme.palette.primary.main,
  },
  upcomingName: {
    fontWeight: 600,
    fontSize: "0.875rem",
    lineHeight: 1.2,
  },
  upcomingMeta: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
}));

const SummaryCard = ({ icon, iconBg, label, value, sub, classes }) => (
  <Paper variant="outlined" className={classes.card}>
    <Box className={classes.cardIcon} style={{ background: iconBg }}>
      {icon}
    </Box>
    <Box flex={1}>
      <Typography className={classes.cardValue}>{value}</Typography>
      <Typography className={classes.cardLabel}>{label}</Typography>
      {sub && <Box className={classes.cardSub}>{sub}</Box>}
    </Box>
  </Paper>
);

const RankingTable = ({ title, rows, maxCount, classes }) => (
  <Paper variant="outlined" style={{ padding: 16, height: "100%" }}>
    <Typography className={classes.sectionTitle}>{title}</Typography>
    {rows.length === 0 ? (
      <Typography variant="body2" color="textSecondary">Sem dados no periodo.</Typography>
    ) : (
      rows.map((row, i) => (
        <Box key={row.id} className={classes.rankRow}>
          <Typography className={classes.rankPosition}>{i + 1}</Typography>
          <Box flex={1}>
            <Typography variant="body2" style={{ fontWeight: 500 }}>{row.name}</Typography>
            <Box className={classes.rankBar} style={{ width: `${(row.count / maxCount) * 100}%` }} />
          </Box>
          <Typography variant="body2" style={{ fontWeight: 600, marginLeft: 8, minWidth: 24, textAlign: "right" }}>
            {row.count}
          </Typography>
        </Box>
      ))
    )}
  </Paper>
);

const AppointmentDashboard = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isAdmin = user.profile === "admin";

  const [period, setPeriod] = useState("week");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/appointments/dashboard", { params: { period } });
      setData(res);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const cards = data?.cards || {};
  const chart = data?.chart || [];
  const topServices = data?.topServices || [];
  const topProviders = data?.topProviders || [];
  const upcoming = data?.upcoming || [];

  const maxServiceCount = topServices[0]?.count || 1;
  const maxProviderCount = topProviders[0]?.count || 1;

  const statusDots = ["confirmado", "concluido", "faltou", "cancelado"]
    .filter(s => cards[s] > 0)
    .map(s => (
      <span key={s} className={classes.statusDot}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: STATUS_COLORS[s], display: "inline-block" }} />
        {cards[s]} {STATUS_LABELS[s].toLowerCase()}
      </span>
    ));

  return (
    <MainContainer>
      <MainHeader>
        <Title>Dashboard de Agendamentos</Title>
      </MainHeader>

      <Box className={classes.root}>
        {/* Period selector */}
        <Box className={classes.periodBar}>
          {PERIODS.map(p => (
            <button
              key={p.key}
              className={`${classes.periodBtn} ${period === p.key ? classes.periodBtnActive : ""}`}
              onClick={() => setPeriod(p.key)}
            >
              {p.label}
            </button>
          ))}
        </Box>

        {loading ? (
          <Box className={classes.loadingBox}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Summary cards */}
            <Grid container spacing={2} style={{ marginBottom: 16 }}>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  classes={classes}
                  icon={<EventNoteIcon style={{ color: "#2563eb", fontSize: 22 }} />}
                  iconBg="rgba(37,99,235,0.1)"
                  label="Agendamentos no periodo"
                  value={cards.total ?? 0}
                  sub={statusDots.length > 0 ? statusDots : null}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  classes={classes}
                  icon={<AttachMoneyIcon style={{ color: "#16a34a", fontSize: 22 }} />}
                  iconBg="rgba(22,163,74,0.1)"
                  label="Receita prevista"
                  value={formatCurrency(cards.revenueForecast ?? 0)}
                  sub={null}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  classes={classes}
                  icon={<CheckCircleOutlineIcon style={{ color: "#16a34a", fontSize: 22 }} />}
                  iconBg="rgba(22,163,74,0.1)"
                  label="Receita realizada"
                  value={formatCurrency(cards.revenueRealized ?? 0)}
                  sub={null}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  classes={classes}
                  icon={<TrendingUpIcon style={{ color: "#ea580c", fontSize: 22 }} />}
                  iconBg="rgba(234,88,12,0.1)"
                  label="Taxa de comparecimento"
                  value={cards.attendanceRate != null ? `${cards.attendanceRate}%` : "—"}
                  sub={null}
                />
              </Grid>
            </Grid>

            {/* Chart */}
            <Paper variant="outlined" className={classes.chartPaper}>
              <Typography className={classes.sectionTitle}>Volume de agendamentos</Typography>
              {chart.length === 0 ? (
                <Typography variant="body2" color="textSecondary">Sem dados no periodo.</Typography>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart
                    data={chart}
                    barSize={period === "today" ? 40 : period === "week" ? 28 : 14}
                    margin={{ top: 4, right: 8, left: -16, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.06)" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <RechartsTooltip
                      formatter={(val, name) => [val, STATUS_LABELS[name] || name]}
                      labelStyle={{ fontWeight: 600 }}
                    />
                    <Legend formatter={(name) => STATUS_LABELS[name] || name} iconType="circle" iconSize={8} />
                    <Bar dataKey="confirmado" stackId="a" fill={STATUS_COLORS.confirmado} />
                    <Bar dataKey="concluido" stackId="a" fill={STATUS_COLORS.concluido} />
                    <Bar dataKey="faltou" stackId="a" fill={STATUS_COLORS.faltou} />
                    <Bar dataKey="cancelado" stackId="a" fill={STATUS_COLORS.cancelado} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>

            {/* Rankings + upcoming */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={isAdmin ? 4 : 6}>
                <RankingTable
                  title="Top servicos"
                  rows={topServices}
                  maxCount={maxServiceCount}
                  classes={classes}
                />
              </Grid>

              {isAdmin && topProviders.length > 0 && (
                <Grid item xs={12} md={4}>
                  <RankingTable
                    title="Top profissionais"
                    rows={topProviders}
                    maxCount={maxProviderCount}
                    classes={classes}
                  />
                </Grid>
              )}

              <Grid item xs={12} md={isAdmin ? 4 : 6}>
                <Paper variant="outlined" style={{ padding: 16 }}>
                  <Typography className={classes.sectionTitle}>Proximos agendamentos</Typography>
                  {upcoming.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">Nenhum agendamento futuro.</Typography>
                  ) : (
                    upcoming.map((appt) => (
                      <Box key={appt.id} className={classes.upcomingRow}>
                        <Box>
                          <Typography className={classes.upcomingTime}>
                            {moment(appt.startAt).format("HH:mm")}
                          </Typography>
                          <Typography variant="caption" style={{ display: "block", color: "#9ca3af", fontSize: "0.7rem" }}>
                            {moment(appt.startAt).format("DD/MM")}
                          </Typography>
                        </Box>
                        <Box flex={1} style={{ minWidth: 0 }}>
                          <Typography className={classes.upcomingName} noWrap>
                            {appt.contact?.name || "—"}
                          </Typography>
                          <Typography className={classes.upcomingMeta} noWrap>
                            {appt.service?.name}{isAdmin && appt.user ? ` · ${appt.user.name}` : ""}
                          </Typography>
                        </Box>
                        <Box
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            flexShrink: 0,
                            background: STATUS_COLORS[appt.status] || "#9ca3af"
                          }}
                        />
                      </Box>
                    ))
                  )}
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </MainContainer>
  );
};

export default AppointmentDashboard;
